import {
  Box,
  Button,
  HStack,
  IconButton,
  Link,
  Popover,
  useDisclosure
} from "@chakra-ui/react"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import classnames from "classnames"
import { forwardRef } from "react"

const DragControl = ({ onToggleDragEnabled, isDragging }) => (
  <Tooltip
    title={isDragging ? "" : "Move"}
    // NOTE: isDisabled doesn't seem to work in conjunction with openDelay
    // isDisabled={isDragging}
    popover={{ openDelay: 750 }}
  >
    <IconButton
      colorPalette="gray"
      variant="ghost"
      size="xxs"
      aria-label="Move text box"
      onMouseDown={() => onToggleDragEnabled(true)}
      onMouseUp={() => onToggleDragEnabled(false)}
      children={<Pathicon icon="arrows-omni" />}
    />
  </Tooltip>
)

const CopyControl = ({ onCopy }) => (
  <Tooltip title="Copy" popover={{ openDelay: 750 }}>
    <IconButton
      colorPalette="gray"
      variant="ghost"
      size="xxs"
      aria-label="Copy text box"
      onClick={() => onCopy()}
      children={<Pathicon icon="copy" />}
    />
  </Tooltip>
)

const DeleteControl = forwardRef(({ onDelete, ...rest }, ref) => (
  <Tooltip title="Delete" popover={{ openDelay: 750 }}>
    <IconButton
      ref={ref}
      colorPalette="gray"
      variant="ghost"
      size="xxs"
      aria-label="Delete text box"
      children={<Pathicon icon="trash" />}
      onClick={() => onDelete()}
      {...rest}
    />
  </Tooltip>
))

const TipControl = () => (
  <Tooltip
    title="Tips for Certificate Creator text blocks"
    popover={{ openDelay: 750 }}
  >
    <IconButton
      colorPalette="gray"
      variant="ghost"
      size="xxs"
      aria-label="Tips for Certificate Creator text blocks"
    >
      <Link
        target="_blank"
        href="http://help.pathwright.com/en/articles/9211185-provide-a-certificate-of-completion"
      >
        <Pathicon icon="info-circle" />
      </Link>
    </IconButton>
  </Tooltip>
)

const DeleteConrolConfirmation = ({ onDelete }) => {
  const { open, onOpen, onClose } = useDisclosure()

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => (e.open ? onOpen() : onClose())}
    >
      <Popover.Trigger>
        <DeleteControl />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content p="10px" bg="rgba(0, 0, 0, 0.8)">
          <Popover.Body>
            <HStack
              gap={2}
              alignItems="center"
              justifyContent="center"
              w="100%"
              h="100%"
            >
              <Button
                variant="solid"
                colorPalette="red"
                size="sm"
                onClick={() => {
                  onClose()
                  // Hack, prevent showing popover on different text object that might
                  // replace this text object's index.
                  setTimeout(() => {
                    onDelete()
                  }, 1)
                }}
              >
                Confirm Delete
              </Button>
              <Button
                variant="solid"
                colorPalette="whiteAlpha"
                size="sm"
                className="Cancel"
                onClick={onClose}
              >
                Cancel
              </Button>
            </HStack>
            <Popover.CloseTrigger>Cancel</Popover.CloseTrigger>
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  )
}

const ObjectControls = ({
  className,
  isFocused,
  isDragging,
  onCopy,
  onDelete,
  onToggleDragEnabled
}) => {
  return (
    <Box
      className={classnames("ObjectControls", className)}
      position="absolute"
      right="0px"
      top="0px"
      zIndex="10"
      opacity={isFocused ? 1 : 0}
      css={{
        [`:has([aria-expanded="true"])`]: {
          opacity: 1
        }
      }}
    >
      <HStack
        gap={2}
        position="absolute"
        right="5px"
        top="50%"
        transform="translateY(-50%)"
        bg="#efefef"
        borderRadius="20px"
        p="5px 10px"
        m="0px 2px"
        w="auto"
        pointerEvents="all"
        fontSize="0.9em"
        transition="opacity 0.1s ease-in"
      >
        <DragControl
          onToggleDragEnabled={onToggleDragEnabled}
          isDragging={isDragging}
        />
        <CopyControl onCopy={onCopy} />
        {!!onDelete && (
          <DeleteConrolConfirmation
            onDelete={() => {
              onDelete()
            }}
          />
        )}
        <TipControl />
      </HStack>
    </Box>
  )
}

ObjectControls.displayName = "ObjectControls"

export default ObjectControls
